import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SearchBar from '../components/SearchBar';
import ResultCard from '../components/ResultCard';
import ModelSelector from '../components/ModelSelector';
import LiveNewsTicker from '../components/LiveNewsTicker';
import PremiumPrompt from '../components/PremiumPrompt';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sparkles, User, LogOut, History, LogIn, TrendingUp, Newspaper, DollarSign, Cpu, Microscope, Heart, Briefcase, Globe, ArrowRight, Phone, Search as SearchIcon, Palette, Upload, Image as ImageIcon, Edit, Mic, MicOff, Share2, Download, Settings, BarChart3, MoreHorizontal } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { id: 'news', name: 'News', icon: Newspaper, color: 'from-blue-500 to-cyan-500', questions: [
    'What are the top news stories today?',
    'Latest developments in global politics',
    'Breaking news in technology sector'
  ]},
  { id: 'finance', name: 'Finance', icon: DollarSign, color: 'from-green-500 to-emerald-500', questions: [
    'Current stock market trends',
    'Cryptocurrency market analysis',
    'Economic indicators this week'
  ]},
  { id: 'technology', name: 'Technology', icon: Cpu, color: 'from-purple-500 to-pink-500', questions: [
    'Latest AI breakthroughs',
    'New smartphone releases',
    'Cloud computing trends'
  ]},
  { id: 'science', name: 'Science', icon: Microscope, color: 'from-indigo-500 to-blue-500', questions: [
    'Recent scientific discoveries',
    'Space exploration updates',
    'Climate change research'
  ]},
  { id: 'health', name: 'Health', icon: Heart, color: 'from-red-500 to-pink-500', questions: [
    'Latest health research findings',
    'Nutrition and wellness tips',
    'Mental health awareness'
  ]},
  { id: 'business', name: 'Business', icon: Briefcase, color: 'from-orange-500 to-yellow-500', questions: [
    'Startup funding news',
    'Business strategy insights',
    'Market expansion opportunities'
  ]}
];

const trendingTopics = [
  { title: 'Artificial Intelligence', queries: 342 },
  { title: 'Climate Change Solutions', queries: 289 },
  { title: 'Quantum Computing', queries: 256 },
  { title: 'Space Exploration', queries: 234 },
  { title: 'Renewable Energy', queries: 198 },
  { title: 'Biotechnology Advances', queries: 187 }
];

const SearchPage = () => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState('sonar');
  const [usage, setUsage] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneResult, setPhoneResult] = useState(null);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneLookupOpen, setPhoneLookupOpen] = useState(false);
  const [searchCount, setSearchCount] = useState(0);
  
  // Image generation state
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imagePrompt, setImagePrompt] = useState('');
  const [imageStyle, setImageStyle] = useState('realistic');
  const [generatingImage, setGeneratingImage] = useState(false);
  
  // Image upload state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [editOperation, setEditOperation] = useState('resize');
  const [editing, setEditing] = useState(false);

  // Voice search state
  const [isRecording, setIsRecording] = useState(false);
  const [voiceDialogOpen, setVoiceDialogOpen] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const fetchUsage = async () => {
    try {
      const response = await axios.get(`${API}/usage`);
      setUsage(response.data);
    } catch (error) {
      console.error('Failed to fetch usage:', error);
    }
  };

  const handleSearch = async (query, generateImage = false) => {
    setLoading(true);
    try {
      const endpoint = user ? `${API}/search` : `${API}/search/public`;
      const headers = user ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(
        endpoint,
        { query, model: selectedModel, generate_image: generateImage },
        { headers }
      );
      
      if (response.data.usage) {
        setUsage(response.data.usage);
      }
      
      setResult(response.data);
      
      if (!user) {
        setSearchCount(prev => prev + 1);
      }
      
      if (!user && response.data.usage?.remaining <= 0) {
        setTimeout(() => {
          toast.error('Daily search limit reached! Sign up for unlimited searches.', {
            duration: 6000,
            action: {
              label: 'Sign Up Free',
              onClick: () => navigate('/auth')
            }
          });
        }, 1000);
      }
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error('Daily search limit reached! Sign up for unlimited searches.', {
          duration: 6000,
          action: {
            label: 'Sign Up Free',
            onClick: () => navigate('/auth')
          }
        });
      } else {
        toast.error(error.response?.data?.detail || 'Search failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleImageGenerated = async (prompt, withImage) => {
    await handleSearch(prompt, withImage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleImageGenerate = async () => {
    if (!imagePrompt.trim()) {
      toast.error('Please enter an image description');
      return;
    }
    
    setGeneratingImage(true);
    const fullPrompt = `${imagePrompt} (Style: ${imageStyle}, high quality, professional)`;
    
    try {
      await handleSearch(fullPrompt, true);
      setImageDialogOpen(false);
      setImagePrompt('');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      toast.error('Failed to generate image');
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/image/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadedImage(response.data);
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  const handleImageEdit = async () => {
    if (!uploadedImage) return;

    setEditing(true);
    try {
      // Convert base64 back to blob for upload
      const base64Data = uploadedImage.data.split(',')[1];
      const blob = await fetch(`data:image/png;base64,${base64Data}`).then(res => res.blob());
      
      const formData = new FormData();
      formData.append('file', blob, 'image.png');
      
      const response = await axios.post(
        `${API}/image/edit?operation=${editOperation}&width=512&height=512`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setUploadedImage(response.data);
      toast.success('Image edited successfully');
    } catch (error) {
      toast.error('Failed to edit image');
    } finally {
      setEditing(false);
    }
  };

  const handleSearchWithImage = async () => {
    if (!uploadedImage) {
      toast.error('Please upload an image first');
      return;
    }
    
    setLoading(true);
    try {
      const searchQuery = `Analyze this uploaded image and provide detailed information about it`;
      await handleSearch(searchQuery, false);
      setUploadDialogOpen(false);
      setUploadedImage(null);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneLookup = async () => {
    if (!phoneNumber.trim()) {
      toast.error('Please enter a phone number');
      return;
    }
    
    setPhoneLoading(true);
    setPhoneResult(null);
    
    try {
      const response = await axios.post(`${API}/phone/lookup`, {
        phone_number: phoneNumber
      });
      setPhoneResult(response.data);
      toast.success('Phone number details retrieved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Phone lookup failed');
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
  };

  const handleQuickSearch = (query) => {
    handleSearch(query, false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Voice search using Web Speech API (more reliable)
  const startVoiceSearch = () => {
    // Check for browser support
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      toast.error('Voice search is not supported in your browser. Try Chrome or Edge.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsRecording(true);
      toast.info('Listening... Speak now');
    };

    recognition.onresult = async (event) => {
      const transcript = event.results[0][0].transcript;
      setIsRecording(false);
      toast.success(`Heard: "${transcript}"`);
      
      // Perform search with the transcript
      await handleSearch(transcript, false);
    };

    recognition.onerror = (event) => {
      setIsRecording(false);
      if (event.error === 'no-speech') {
        toast.error('No speech detected. Please try again.');
      } else if (event.error === 'not-allowed') {
        toast.error('Microphone access denied. Please allow microphone access.');
      } else {
        toast.error('Voice recognition error. Please try again.');
      }
    };

    recognition.onend = () => {
      setIsRecording(false);
    };

    try {
      recognition.start();
    } catch (error) {
      toast.error('Failed to start voice recognition');
      setIsRecording(false);
    }
  };

  const stopVoiceSearch = () => {
    setIsRecording(false);
  };

  // Share search handler
  const handleShareSearch = async () => {
    if (!result || !user) {
      toast.error('Please sign in to share searches');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/search/share`, 
        { search_id: result.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const shareUrl = `${window.location.origin}/shared/${response.data.share_id}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied to clipboard!');
    } catch (error) {
      toast.error('Failed to create share link');
    }
  };

  // Export history handler
  const handleExportHistory = async (format = 'json') => {
    if (!user) {
      toast.error('Please sign in to export history');
      return;
    }
    
    try {
      const response = await axios.get(`${API}/search/export?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = format === 'json' 
        ? JSON.stringify(response.data.data, null, 2) 
        : response.data.data;
      
      const blob = new Blob([data], { type: format === 'json' ? 'application/json' : 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('History exported successfully');
    } catch (error) {
      toast.error('Failed to export history');
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#1a1030] via-[#0b0b10] to-black">
      <PremiumPrompt searchCount={searchCount} />
      
      <nav className="border-b border-border/50 backdrop-blur-md bg-card/30 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2 min-w-0">
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer min-w-0 shrink" onClick={() => window.location.reload()}>
            <Sparkles className="text-primary" size={24} />
            <h1 className="text-base sm:text-2xl font-heading font-bold text-foreground truncate" data-testid="app-title">
              concurrenz<span className="text-primary">.AI</span>
            </h1>
          </div>

          <div className="flex items-center gap-0.5 sm:gap-2 shrink-0">
            {/* Mobile tools menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden h-9 w-9" data-testid="mobile-more-tools" aria-label="More tools">
                  <MoreHorizontal size={18} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => setImageDialogOpen(true)}>
                  <Palette size={16} className="mr-2" /> Generate image
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setUploadDialogOpen(true)}>
                  <Upload size={16} className="mr-2" /> Upload & edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setPhoneLookupOpen(true)}>
                  <Phone size={16} className="mr-2" /> Phone lookup
                </DropdownMenuItem>
                <DropdownMenuItem onClick={isRecording ? stopVoiceSearch : startVoiceSearch}>
                  {isRecording ? <MicOff size={16} className="mr-2" /> : <Mic size={16} className="mr-2" />}
                  Voice search
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Voice Search Button */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={isRecording ? stopVoiceSearch : startVoiceSearch}
              className={`${isRecording ? 'text-red-500 animate-pulse bg-red-500/10' : ''} hidden md:inline-flex h-9 w-9 sm:h-10 sm:w-10`}
              data-testid="voice-search-button"
              title="Voice Search"
            >
              {isRecording ? <MicOff size={18} /> : <Mic size={18} />}
            </Button>

            <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="image-gen-button" className="hidden md:inline-flex h-9 w-9 sm:h-10 sm:w-10">
                  <Palette size={18} />
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-[calc(100vw-1.5rem)] sm:max-w-md mx-2 sm:mx-4">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">Generate Image</DialogTitle>
                  <DialogDescription>Create AI-generated images from text descriptions</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Input
                      data-testid="image-prompt-input"
                      value={imagePrompt}
                      onChange={(e) => setImagePrompt(e.target.value)}
                      placeholder="Describe the image you want to create..."
                      className="w-full"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Style</label>
                    <Select value={imageStyle} onValueChange={setImageStyle}>
                      <SelectTrigger data-testid="image-style-selector">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="realistic">Realistic</SelectItem>
                        <SelectItem value="artistic">Artistic</SelectItem>
                        <SelectItem value="cartoon">Cartoon</SelectItem>
                        <SelectItem value="abstract">Abstract</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Button 
                    onClick={handleImageGenerate} 
                    disabled={generatingImage || !imagePrompt.trim()}
                    className="w-full rounded-full"
                    data-testid="generate-image-button"
                  >
                    {generatingImage ? 'Generating...' : 'Generate Image'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Image Upload & Edit Dialog */}
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="image-upload-button" title="Upload & Edit Image" className="hidden md:inline-flex h-9 w-9">
                  <Upload size={20} />
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-[calc(100vw-1.5rem)] sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">Upload & Edit Image</DialogTitle>
                  <DialogDescription>Upload images to edit or use in search queries</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="image-upload-input"
                    />
                    <label
                      htmlFor="image-upload-input"
                      className="cursor-pointer flex flex-col items-center gap-2"
                      data-testid="upload-area"
                    >
                      <ImageIcon className="text-primary" size={48} />
                      <p className="text-sm font-medium">Click to upload image</p>
                      <p className="text-xs text-muted-foreground">PNG, JPG up to 10MB</p>
                    </label>
                  </div>

                  {uploadedImage && (
                    <Card className="p-4 space-y-4">
                      <img
                        src={uploadedImage.data}
                        alt="Uploaded"
                        className="w-full rounded-lg max-h-64 object-contain"
                        data-testid="uploaded-image-preview"
                      />
                      <div className="text-xs text-muted-foreground">
                        {uploadedImage.filename} • {uploadedImage.size?.width}x{uploadedImage.size?.height}
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium">Edit Operation</label>
                        <Select value={editOperation} onValueChange={setEditOperation}>
                          <SelectTrigger data-testid="edit-operation-selector">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="resize">Resize (512x512)</SelectItem>
                            <SelectItem value="rotate">Rotate 90°</SelectItem>
                            <SelectItem value="grayscale">Grayscale</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          onClick={handleImageEdit}
                          disabled={editing}
                          variant="outline"
                          className="flex-1 rounded-full"
                          data-testid="apply-edit-button"
                        >
                          <Edit size={16} className="mr-2" />
                          {editing ? 'Applying...' : 'Apply Edit'}
                        </Button>
                        <Button
                          onClick={handleSearchWithImage}
                          disabled={loading}
                          className="flex-1 rounded-full"
                          data-testid="search-with-image-button"
                        >
                          <SearchIcon size={16} className="mr-2" />
                          Search with Image
                        </Button>
                      </div>
                    </Card>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={phoneLookupOpen} onOpenChange={setPhoneLookupOpen}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="phone-lookup-button" className="hidden md:inline-flex h-9 w-9">
                  <Phone size={20} />
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-[calc(100vw-1.5rem)] sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">Phone Number Lookup</DialogTitle>
                  <DialogDescription>Get carrier, country, and type information for any phone number</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="flex gap-2">
                    <Input
                      data-testid="phone-input"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="+1234567890"
                      className="flex-1"
                    />
                    <Button 
                      onClick={handlePhoneLookup} 
                      disabled={phoneLoading}
                      data-testid="phone-lookup-submit"
                    >
                      <SearchIcon size={18} />
                    </Button>
                  </div>
                  
                  {phoneResult && (
                    <Card className="p-4 space-y-2" data-testid="phone-result">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-muted-foreground">Number</p>
                          <p className="font-medium">{phoneResult.phone_number}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Type</p>
                          <p className="font-medium">{phoneResult.type}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Country</p>
                          <p className="font-medium">{phoneResult.country}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Carrier</p>
                          <p className="font-medium">{phoneResult.carrier}</p>
                        </div>
                      </div>
                    </Card>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {user ? (
              <>
                {(user.role === 'admin' || user.role === 'super_admin') && (
                  <>
                    <Button
                      data-testid="dashboard-button"
                      onClick={() => navigate('/dashboard')}
                      variant="ghost"
                      className="font-medium"
                    >
                      Dashboard
                    </Button>
                    <Button
                      data-testid="analytics-button"
                      onClick={() => navigate('/analytics')}
                      variant="ghost"
                      size="icon"
                      title="Analytics"
                    >
                      <BarChart3 size={20} />
                    </Button>
                  </>
                )}

                <Button
                  data-testid="history-button"
                  onClick={() => navigate('/history')}
                  variant="ghost"
                  size="icon"
                  title="History"
                >
                  <History size={20} />
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button data-testid="user-menu-button" variant="ghost" size="icon" className="rounded-full">
                      <User size={20} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem disabled className="font-medium">
                      {user.full_name}
                      <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary capitalize">
                        {user.role?.replace('_', ' ')}
                      </span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-menu-item">
                      <Settings size={16} className="mr-2" />
                      Settings
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExportHistory('json')} data-testid="export-json-menu-item">
                      <Download size={16} className="mr-2" />
                      Export History (JSON)
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExportHistory('csv')} data-testid="export-csv-menu-item">
                      <Download size={16} className="mr-2" />
                      Export History (CSV)
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                      <LogOut size={16} className="mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button
                data-testid="signin-button"
                onClick={() => navigate('/auth')}
                variant="ghost"
                className="font-medium text-sm px-2 sm:px-4 h-9"
              >
                <LogIn size={16} className="mr-1 sm:mr-2" />
                <span className="sm:hidden">Login</span>
                <span className="hidden sm:inline">Sign In</span>
              </Button>
            )}
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-12 overflow-x-hidden">
        <div className="space-y-8 sm:space-y-12">
          <div className="text-center space-y-4 sm:space-y-6" data-testid="search-hero">
            {!result && (
              <>
                <h2 className="text-2xl sm:text-4xl md:text-6xl font-heading font-bold tracking-tight text-foreground px-2">
                  What can I help you
                  <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"> discover</span>?
                </h2>
                <p className="text-sm sm:text-lg md:text-xl text-muted-foreground font-body max-w-2xl mx-auto px-4">
                  Ask anything and get intelligent answers powered by advanced AI
                </p>
              </>
            )}

            <div className="space-y-4">
              <SearchBar onSearch={handleSearch} loading={loading} />
              
              <div className="flex items-center justify-center gap-2 sm:gap-3">
                <ModelSelector selectedModel={selectedModel} onModelSelect={setSelectedModel} />
              </div>
            </div>
          </div>

          {result && (
            <div className="animate-in fade-in duration-500 space-y-6">
              <ResultCard result={result} />
            </div>
          )}

          {!result && (
            <>
              {/* Live News Ticker */}
              <div data-testid="news-section">
                <LiveNewsTicker />
              </div>

              <div className="space-y-6" data-testid="trending-section">
                <div className="flex items-center gap-3">
                  <TrendingUp className="text-primary" size={24} />
                  <h3 className="text-2xl font-heading font-bold text-foreground">Trending Now</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {trendingTopics.map((topic, idx) => (
                    <Card
                      key={idx}
                      onClick={() => handleQuickSearch(topic.title)}
                      className="p-4 rounded-xl border bg-card/50 backdrop-blur-sm hover:bg-card/80 hover:border-primary/50 transition-all cursor-pointer group"
                      data-testid={`trending-topic-${idx}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-heading font-semibold text-foreground group-hover:text-primary transition-colors">
                            {topic.title}
                          </h4>
                          <p className="text-sm text-muted-foreground mt-1">{topic.queries} searches</p>
                        </div>
                        <ArrowRight className="text-muted-foreground group-hover:text-primary transition-colors" size={20} />
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              <div className="space-y-8" data-testid="categories-section">
                <div className="flex items-center gap-3">
                  <Globe className="text-primary" size={24} />
                  <h3 className="text-2xl font-heading font-bold text-foreground">Explore by Category</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categories.map((category) => {
                    const Icon = category.icon;
                    return (
                      <Card
                        key={category.id}
                        className="p-6 rounded-xl border bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all group"
                        data-testid={`category-${category.id}`}
                      >
                        <div className="space-y-4">
                          <div className="flex items-center gap-3">
                            <div className={`p-3 rounded-lg bg-gradient-to-br ${category.color}`}>
                              <Icon className="text-white" size={24} />
                            </div>
                            <h4 className="text-xl font-heading font-bold text-foreground">{category.name}</h4>
                          </div>
                          
                          <div className="space-y-2">
                            {category.questions.map((question, idx) => (
                              <button
                                key={idx}
                                onClick={() => handleQuickSearch(question)}
                                className="w-full text-left p-3 rounded-lg bg-background/50 hover:bg-background text-sm text-muted-foreground hover:text-primary transition-all group/item"
                                data-testid={`category-question-${category.id}-${idx}`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-body">{question}</span>
                                  <ArrowRight className="text-muted-foreground group-hover/item:text-primary transition-colors flex-shrink-0 ml-2" size={16} />
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-6" data-testid="quick-actions-section">
                <h3 className="text-2xl font-heading font-bold text-foreground text-center">Popular Searches</h3>
                <div className="flex flex-wrap justify-center gap-3">
                  {[
                    'How does photosynthesis work?',
                    'Explain quantum mechanics simply',
                    'Best programming languages 2026',
                    'History of the internet',
                    'Future of renewable energy',
                    'Machine learning basics'
                  ].map((query, idx) => (
                    <Button
                      key={idx}
                      onClick={() => handleQuickSearch(query)}
                      variant="outline"
                      className="rounded-full border-primary/30 hover:border-primary hover:bg-primary/10 transition-all"
                      data-testid={`popular-search-${idx}`}
                    >
                      {query}
                    </Button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default SearchPage;
